import { useEffect, useRef, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "@mui/material/Table";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";
import useApi from "../../../components/Utils/api";
import MDInput from "../../../components/MDInput";
import toast from "react-hot-toast";
import * as React from "react";
import {Controller, FormProvider, useForm} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdrDefaultModel, mdrDefaultSchema } from "../../../models/MdrDefaultMaster";
import {FormControl, InputLabel, Select, OutlinedInput} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import PermissionControl from "../../../components/Utils/PermissionControl";

function MdrDefaultMasterNew() {
  const [masters, setMasters] = useState([]);
  const paymentTypes = [
    { label: "Visa Domestic Credit", namePrefix: "visaDomesticCredit" },
    { label: "Visa Domestic Debit", namePrefix: "visaDomesticDebit" },
    { label: "Visa International", namePrefix: "visaInternational" },
    { label: "Mastercard Dom Credit", namePrefix: "mastercardDomesticCredit" },
    { label: "Mastercard Dom Debit", namePrefix: "mastercardDomesticDebit" },
    { label: "Mastercard International", namePrefix: "mastercardInternational" },
    { label: "eftpos Australia", namePrefix: "eftposAustralia" },
    { label: "Discover (Diners)", namePrefix: "discoverDiners" },
    { label: "Amex", namePrefix: "amex" },
    { label: "UPI", namePrefix: "upi" },
    { label: "JCB", namePrefix: "jcb" },
  ];

  const { register, handleSubmit, reset, formState: { errors }, trigger, watch, control, setValue } = useForm({
    mode: "onChange",
    resolver: yupResolver(mdrDefaultSchema),
    defaultValues: {
      ...paymentTypes.reduce((acc, type) => ({
        ...acc,
        [`${type.namePrefix}_msf_rate`]: "0",
        [`${type.namePrefix}_msf_value`]: "0.00"
      }), {})
    }
  });

  const inputStyles = {
    '& .MuiInputBase-root': {
      height: '32px',
      fontSize: '0.875rem',
      padding: '0 8px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
      lineHeight: '32px',
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.75rem',
    },
  };

  const settlementType = watch("settlementType");

  const api = useApi();

  function updateMasterHandler(data) {
    api.post("master/updateDefaultMdrMaster", data)
      .then(response => {
        if(response.data.statusCodeValue === 200){
          toast.success(response.data.body.message);
        }
      })
      .catch(error => {
        toast.error(error.response.data.body);
      })
  }

  function updateMasterICCHandler(data) {
    api.post("master/updateDefaultMdrMasterICC", data)
      .then(response => {
        if(response.data.statusCodeValue === 200){
          toast.success(response.data.body.message);
        }
      })
      .catch(error => {
        toast.error(error.response.data.body);
      })
  }

  const submitHandler = () => {
    handleSubmit(updateMasterHandler)();
  }

  const submitHandlerIC = () => {
    handleSubmit(updateMasterICCHandler)();
  }

  const getMasters = () => {
    api.post("master/getDefaultMdrMaster")
      .then((response) => {
        if(response.data.statusCodeValue === 200) {
          setMasters(response.data.body[0]);
          reset(response.data.body[0]);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        toast.error(error.response.data.body);
      })
  }

  useEffect(() => {
    getMasters();
  }, []);

  useEffect(() => {
    if (settlementType === "NET") {
      paymentTypes.forEach((type) => {
        setValue(`${type.namePrefix}_mdrType`, "Blended");
      });
    }
  }, [settlementType, setValue]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <FormProvider {...{ register, handleSubmit, reset, formState: { errors }, trigger, watch, control }}>
            <Grid item xs={12}>
              <h5>Default MDR Configuration</h5>
            </Grid>

            {/* Settlement Type */}
            <Grid item xs={12}>
              <FormControl fullWidth sx={inputStyles}>
                <InputLabel id="settlement-label">Settlement Type</InputLabel>
                <Controller
                  name="settlementType"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select {...field} labelId="settlement-label" input={<OutlinedInput label="Settlement Type" />}>
                      <MenuItem value="">- Select Settlement Type -</MenuItem>
                      <MenuItem value="GROSS">Gross Settlement</MenuItem>
                      <MenuItem value="NET">Net Settlement</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            {/* Dynamic Payment Types */}
            {paymentTypes.map((type) => (
              <React.Fragment key={type.namePrefix}>
                <Grid item xs={3}>
                  <MDTypography
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      border: '1px solid black',
                      padding: '4px',
                      borderRadius: '4px',
                    }}
                  >
                    {type.label}
                  </MDTypography>
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth sx={inputStyles}>
                    <InputLabel id={`${type.namePrefix}_mdr_label`}></InputLabel>
                    <Controller
                      name={`${type.namePrefix}_mdrType`}
                      control={control}
                      defaultValue="Blended"
                      render={({ field }) => (
                        <Select
                          {...field}
                          labelId={`${type.namePrefix}_mdr_label`}
                          input={<OutlinedInput label="" />}
                          disabled={settlementType === "NET"}
                          value={settlementType === "NET" ? "Blended" : field.value}
                        >
                          <MenuItem value="---"></MenuItem>
                          <MenuItem value="Blended">Blended</MenuItem>
                          <MenuItem value="IC++">IC++</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <Controller
                    name={`${type.namePrefix}_msf_rate`}
                    control={control}
                    defaultValue="0"
                    render={({ field }) => (
                      <MDInput
                        {...field}
                        type="text"
                        label="MSF Rate %"
                        error={errors[`${type.namePrefix}_msf_rate`]}
                        helperText={errors[`${type.namePrefix}_msf_rate`] ? errors[`${type.namePrefix}_msf_rate`].message : ''}
                        fullWidth
                        sx={inputStyles}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Controller
                    name={`${type.namePrefix}_msf_value`}
                    control={control}
                    defaultValue="0.00"
                    render={({ field }) => (
                      <MDInput
                        {...field}
                        type="text"
                        label="Fee $"
                        error={errors[`${type.namePrefix}_msf_value`]}
                        helperText={errors[`${type.namePrefix}_msf_value`] ? errors[`${type.namePrefix}_msf_value`].message : ''}
                        fullWidth
                        sx={inputStyles}
                      />
                    )}
                  />
                </Grid>
              </React.Fragment>
            ))}
          </FormProvider>

          <Grid container justifyContent="flex-end" style={{ marginTop: '10px'}}>
            <PermissionControl permission={{ restrict: true, module: "Masters", subModule: "UpdateDefaultMdrMaster" }}>
              <MDButton variant="gradient" onClick={() => submitHandler()} color="info" style={{marginBottom: "12px"}}>
                Update
              </MDButton>
            </PermissionControl>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default MdrDefaultMasterNew;
